import { Injectable } from '@angular/core';
import type { PageEntry, PageTransformada, ModyoAsset } from '@core/interfaces/page.interface';

@Injectable({
  providedIn: 'root',
})
export class ServicioTransformadorPage {
  public transformar(entry: PageEntry): PageTransformada {
    return {
      meta: this.extraerMeta(entry),
      assets: this.extraerAssets(entry),
      contenido: this.extraerContenido(entry),
      beneficios: this.extraerBeneficios(entry),
      callToActions: this.extraerCallToActions(entry),
      navegacion: this.extraerNavegacion(entry),
      seo: this.extraerSeo(entry),
      analytics: this.extraerAnalytics(entry),
      configuracion: this.extraerConfiguracion(entry),
    };
  }

  private extraerMeta(entry: PageEntry): {
    uuid: string;
    slug: string;
    titulo: string;
    fecha_publicacion?: string;
    fecha_actualizacion?: string;
  } {
    return {
      uuid: entry.meta.uuid,
      slug: entry.meta.slug,
      titulo: entry.fields.titulo || entry.meta.name,
      fecha_publicacion: entry.meta.published_at,
      fecha_actualizacion: entry.meta.updated_at,
    };
  }

  private extraerAssets(entry: PageEntry): {
    logoHeader?: ModyoAsset;
    imagenHero?: ModyoAsset;
    imagenDestacada?: ModyoAsset;
    iconoPrincipal?: ModyoAsset;
    iconoStepper?: ModyoAsset;
  } {
    return {
      logoHeader: entry.fields.logo_header,
      imagenHero: entry.fields.imagen_hero,
      imagenDestacada: entry.fields.imagen_destacada,
      iconoPrincipal: entry.fields.icono_principal,
      iconoStepper: entry.fields.icono_stepper,
    };
  }

  private extraerContenido(entry: PageEntry): {
    tituloPrincipal?: string;
    subtitulo?: string;
    descripcion?: string;
  } {
    return {
      tituloPrincipal: entry.fields.titulo_principal,
      subtitulo: entry.fields.subtitulo,
      descripcion: entry.fields.descripcion,
    };
  }

  private extraerBeneficios(entry: PageEntry): Array<{
    titulo: string;
    descripcion: string;
  }> {
    const beneficios = [];

    if (entry.fields.beneficio_1_titulo && entry.fields.beneficio_1_descripcion) {
      beneficios.push({
        titulo: entry.fields.beneficio_1_titulo,
        descripcion: entry.fields.beneficio_1_descripcion,
      });
    }

    if (entry.fields.beneficio_2_titulo && entry.fields.beneficio_2_descripcion) {
      beneficios.push({
        titulo: entry.fields.beneficio_2_titulo,
        descripcion: entry.fields.beneficio_2_descripcion,
      });
    }

    if (entry.fields.beneficio_3_titulo && entry.fields.beneficio_3_descripcion) {
      beneficios.push({
        titulo: entry.fields.beneficio_3_titulo,
        descripcion: entry.fields.beneficio_3_descripcion,
      });
    }

    return beneficios;
  }

  private extraerCallToActions(entry: PageEntry): {
    botonPrincipal?: {
      texto: string;
      accion: string;
    };
    linkSecundario?: {
      texto: string;
      url: string;
    };
  } {
    return {
      botonPrincipal:
        entry.fields.boton_principal_texto && entry.fields.boton_principal_accion
          ? {
              texto: entry.fields.boton_principal_texto,
              accion: entry.fields.boton_principal_accion,
            }
          : undefined,
      linkSecundario:
        entry.fields.link_secundario_texto && entry.fields.link_secundario_url
          ? {
              texto: entry.fields.link_secundario_texto,
              url: entry.fields.link_secundario_url,
            }
          : undefined,
    };
  }

  private extraerNavegacion(entry: PageEntry):
    | {
        pasoActual: number;
        totalPasos: number;
        textoPaso: string;
      }
    | undefined {
    if (!entry.fields.paso_actual || !entry.fields.total_pasos) {
      return undefined;
    }

    return {
      pasoActual: entry.fields.paso_actual,
      totalPasos: entry.fields.total_pasos,
      textoPaso: entry.fields.texto_paso ?? `Paso ${entry.fields.paso_actual} de ${entry.fields.total_pasos}`,
    };
  }

  private extraerSeo(entry: PageEntry):
    | {
        titulo?: string;
        descripcion?: string;
        palabrasClave?: string[];
      }
    | undefined {
    if (!entry.fields.seo_titulo && !entry.fields.seo_descripcion) {
      return undefined;
    }

    const emptyArray: string[] = [];

    return {
      titulo: entry.fields.seo_titulo,
      descripcion: entry.fields.seo_descripcion,
      palabrasClave: entry.fields.seo_palabras_clave?.split(',').map((k) => k.trim()) ?? emptyArray,
    };
  }

  private extraerAnalytics(entry: PageEntry):
    | {
        pageName?: string;
        categoria?: string;
        eventosTracking?: Record<string, unknown>;
      }
    | undefined {
    if (!entry.fields.analytics_page_name) {
      return undefined;
    }

    let eventosTracking: Record<string, unknown> | undefined;
    if (entry.fields.eventos_tracking) {
      try {
        eventosTracking = JSON.parse(entry.fields.eventos_tracking) as Record<string, unknown>;
      } catch {
        /* Ignorar errores de parseo */
      }
    }

    return {
      pageName: entry.fields.analytics_page_name,
      categoria: entry.fields.analytics_categoria,
      eventosTracking,
    };
  }

  private extraerConfiguracion(entry: PageEntry): {
    requiereAutenticacion?: boolean;
    estado?: string;
    cacheTtl?: number;
  } {
    const defaultRequiereAutenticacion = false;

    return {
      requiereAutenticacion: entry.fields.requiere_autenticacion ?? defaultRequiereAutenticacion,
      estado: entry.fields.estado ?? entry.meta.status,
      cacheTtl: entry.fields.cache_ttl,
    };
  }
}
